import * as EJS from 'ejs';
import * as fs from 'fs';
import * as process from 'process';
import {Log, logErr, LogLn, Util} from './Util';
import * as jsbeautify from 'js-beautify';
import * as fse from 'fs-extra';
import {AppJson, View, AppConfig, AppManifest, ComponentId, RouteJson} from './types/App';
import {
    CtrlButtonTypeGui, MgControlType, SideType, StorageAttributeType, TableWebStyle, WindowType, WindowPosition,
    CtrlImageStyle
} from './types/enums/Enums';
import {Themes} from './types/Themes';

import * as _ from 'lodash';

import {TemplateConfig} from "./types/TemplateConfig";
import {getTemplateConfig, prettierConfig} from './config';

import * as prettier from "prettier";
import {GeneratedFileTypes} from './types/enums/GeneratedFileTypes';
import {processJson} from './ProcessJson';
import {FileAPI} from './FileAPI';
//import {exec} from "child_process";

const htmlBeautify = jsbeautify.html;
const cssBeautify  = jsbeautify.css;

// @dynamic
export class Factory
{
	static app: AppJson;
	static magic_config: AppConfig;
	static manifest: AppManifest;
    static routeCollection: RouteJson;

	////////////////////////////////////////////////
	// input point for the entire generation process
	////////////////////////////////////////////////
	static init(): void {
		// get the input directory from the command line argument
		FileAPI.inputRoot = process.argv[2] + '\\';

		LogLn("input: " + FileAPI.inputRoot);

		// read the config.json file
		this.magic_config = FileAPI.readInputConfig();

		// read the views (= forms) json file
		this.app = FileAPI.readInputJson(this.magic_config);

		// initialize the output directory
		FileAPI.destDir = this.magic_config.output_folder + '\\' + this.app.name;

		LogLn("output: " + FileAPI.destDir);

		// process the views objects, get the classes used for each control
		LogLn("prepare classes...");
		this.app = processJson(this.app);

		// copy the skeleton of the new project, if needed
		FileAPI.prepareSkeleton(this.app, this.magic_config.create_new_project);

		// create an updated manifest
		this.manifest = this.prepareManifest();

		// build the files for each and every view
		this.buildViews(this.magic_config.output_folder + '\\');

        if (this.magic_config.create_route_map) {
            //read the routes json file
            this.routeCollection = FileAPI.readRouteJson();
            // write the routes to file
            this.buildAndWriteRoutesToFile(this.magic_config.output_folder + '\\');
        }

        // copy the app module files
    	FileAPI.CopyAppModuleFiles(this.magic_config.theme);

		// // copy css files to output projects
		// if (this.magic_config.created_application_css)
		// 	FileAPI.copyCssFile();

		// copy images to output project
		FileAPI.copyImages(this.magic_config);
	};

	////////////////////////////////////////////////
	// build the files for all views
	////////////////////////////////////////////////
	static buildViews(outputDir: string): void {
		let app   = this.app;
		app.views = app.views || [];

		Log("Building views: ");
		// loop on all views (=forms)
		app.views.forEach((view: View) => {
			const
				viewId                           = view.props.id,
				dest                             = outputDir + `${this.app.name}/src/app/` + view.props.component_path,

				data                             = {
					MgControlType    : MgControlType,
					theme            : this.magic_config.theme,
					CtrlButtonTypeGui: CtrlButtonTypeGui,
					SideType         : SideType,
                    StorageAttributeType: StorageAttributeType,
                    TableWebStyle    : TableWebStyle,
					WindowType		 : WindowType,
					WindowPosition   : WindowPosition,
                    Util             : Util,
					ctrl             : view,
					app              : app,
					_                : _,
					magicConfig      : this.magic_config,
                    CtrlImageStyle   : CtrlImageStyle
				},

				// templates for creating different output files
				componentFiles: TemplateConfig[] = [
					{
						type       : GeneratedFileTypes.TS,
						template   : './src/templates/component/view.component.ts',
						destination: dest,
						name       : `${viewId}.component.ts`,
						data       : data

					},
					{
						type       : GeneratedFileTypes.HTML,
						destination: dest,
						template   : './src/templates/component/view.component.html',
						name       : `${viewId}.component.html`,
						data       : data
					},
					{
						type       : GeneratedFileTypes.CSS,
						destination: dest,
						template   : './src/templates/component/view.component.css',
						name       : `${viewId}.component.css`,
						data       : data
					}
				];

			// run the "generate" function for each file type for the current view
			Log(viewId + " ");
			componentFiles.forEach(templateConfig => this.generate(templateConfig));
		});
        getTemplateConfig(app, this.magic_config, this.manifest).forEach(templateConfig => this.generate(templateConfig));
		LogLn("Done");
	};

	////////////////////////////////////////////////
	// generate file using the specified template
	////////////////////////////////////////////////
	static generate(file: TemplateConfig) {
		//Log(`>> generating file ${file.name}...\t\t`);
		EJS.renderFile(file.template, file.data, {}, (err, output) => {
			if (err)
			{
				logErr(err);
			}

			if (file.type === GeneratedFileTypes.HTML)
			{
				// output = output.replace(/(\r\n|\n|\r)/gm, "");
			}

			const
				fnMap = {
					[GeneratedFileTypes.HTML]: htmlBeautify.bind(this, output, {
						"max_preserve_newlines": 0,
						"wrap_line_length"     : 0,
						"wrap_attributes"      : "force-expand-multiline"
					}),
					[GeneratedFileTypes.TS]  : prettier.format.bind(this, output, prettierConfig),
					[GeneratedFileTypes.CSS] : cssBeautify.bind(this, output, {})
				};

			try
			{
				output = fnMap[file.type]();

			}
			catch (err)
			{

				logErr(output);
				logErr(err);

			}

			fse.ensureDirSync(file.destination);
			fs.writeFileSync(`${file.destination}/${file.name}`, output);
			//LogLn('[success]');
		});

	}

    /////////////////////////////////////////////////////
    // build and write the routes to file
    /////////////////////////////////////////////////////
    static buildAndWriteRoutesToFile(outputDir: string) {
        LogLn("Building routes ");
        let routeCollection = this.routeCollection;
        routeCollection.routesArray = routeCollection.routesArray || [];
        const data = {
            routesArray: routeCollection.routesArray
        }, file = {
            type: GeneratedFileTypes.TS,
            template: './src/templates/angular/src/Routes.ts',
            destination: outputDir + `${this.app.name}/src/app/`,
            name: 'routes.ts',
            data: data
        };
        EJS.renderFile(file.template, file.data, {}, (err, output) => {
            if (err) {
                logErr(err);
            }
            const fnMap = {
                [GeneratedFileTypes.TS]: prettier.format.bind(this, output, prettierConfig)
            };
            try {
                output = fnMap[file.type]();
            }
            catch (err) {
                logErr(output);
                logErr(err);
            }
            fse.ensureDirSync(file.destination);
            fs.writeFileSync(`${file.destination}/${file.name}`, output);
        });
        LogLn("Done building routes");
    }

	/////////////////////////////////////////////////////
	// prepare the manifest with the new generation info
	/////////////////////////////////////////////////////
	static prepareManifest(): AppManifest {
		let manifest: AppManifest = FileAPI.getOldManifest(this.magic_config, this.app.name);
		this.AddViewsToManifest(manifest);
		FileAPI.WriteManifest(manifest, this.app.name);

		return manifest;
    }

	////////////////////////////////////////////////
	// add the current views to the new manifest
	////////////////////////////////////////////////
	static AddViewsToManifest(manifest: AppManifest): void {
		this.app.views.forEach((view: View) => {
            let compid: ComponentId = {path: '', name: '', uniqueName: ''};
            compid.path = view.props.component_path;
            compid.name = view.props.id;
            compid.uniqueName = view.props.component_uniquename;

            if (manifest.components.findIndex(x => x.name === compid.name && x.path == compid.path) < 0)
			{
				manifest.components.push(compid);
			}
		});

		console.log("Adding components to manifest" );
	}
}

//Factory.init();











